import type {
  AddZonePricesBody,
  AddZonePricesParams,
  AssignAssortmentsToCategoryBody,
  AssignAssortmentsToCategoryParams,
  AssortmentCategory,
  AssortmentPrice,
  BatchUpdateAssortmentPriceItem,
  BatchUpdateAssortmentPricesParams,
  BatchUpdateZonePriceAdjustmentItem,
  BatchUpdateZonePriceAdjustmentsParams,
  CopySalesAgreementPricePeriodBody,
  CopySalesAgreementPricePeriodParams,
  CreateAssortmentCategoryBody,
  CreateAssortmentCategoryParams,
  CreateSalesAgreementAssortmentPriceBody,
  CreateSalesAgreementAssortmentPriceParams,
  CreateSalesAgreementPricePeriodBody,
  CreateSalesAgreementPricePeriodParams,
  DeleteAssortmentCategoryParams,
  DeleteSalesAgreementAssortmentPriceParams,
  DeleteZonePriceParams,
  GetSalesAgreementAssortmentCategoriesParams,
  GetSalesAgreementAssortmentPricesParams,
  GetSalesAgreementPriceListsParams,
  GetSalesAgreementPricePeriodParams,
  GetSalesAgreementPricePeriodsParams,
  GetSalesAgreementsParams,
  GetSalesAgreementsResponse,
  GetTimberBuyerPricePeriodsParams,
  MainAssortmentModel,
  PriceListItem,
  PricePeriod,
  PricePeriodDetail,
  RenameAssortmentCategoryBody,
  RenameAssortmentCategoryParams,
  SalesAgreement,
  SalesAgreementSummary,
  TimberBuyerPricePeriod,
  UnassignAssortmentFromCategoryParams,
  UpdateSalesAgreementAssortmentPriceBody,
  UpdateSalesAgreementAssortmentPriceParams,
  UpdateSalesAgreementBody,
  UpdateSalesAgreementPricePeriodBody,
  UpdateSalesAgreementPricePeriodParams,
  ZonePrice,
  ZonePricesParams,
} from '@/types/TimberBuyer'
import {
  ApiClient,
  ApiError,
  apiDelete,
  apiGetWithPagedResult,
  type PagedResult,
} from '../ApiClient'

export async function getSalesAgreements(
  params: GetSalesAgreementsParams,
): Promise<GetSalesAgreementsResponse> {
  const queryParams: Record<string, string> = {}
  if (params.sort) queryParams.sort = params.sort
  if (params.status) queryParams.status = params.status

  const response = await ApiClient.get<SalesAgreementSummary[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements`,
    {
      headers: {
        'x-tenant-id': params.tenantId,
        'x-page-size': params.pageSize.toString(),
        ...(params.continuationToken ? { 'x-continuation-token': params.continuationToken } : {}),
      },
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: continuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export async function getSalesAgreement(
  timberBuyerId: string,
  salesAgreementId: string,
): Promise<SalesAgreement> {
  const response = await ApiClient.get<SalesAgreement>(
    `/timber-buyers/${encodeURIComponent(timberBuyerId)}/sales-agreements/${encodeURIComponent(salesAgreementId)}`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function getSalesAgreementPriceLists(
  params: GetSalesAgreementPriceListsParams,
): Promise<PriceListItem[]> {
  const response = await ApiClient.get<PriceListItem[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/price-lists`,
    {
      params: {
        year: String(params.year),
        agreementType: params.agreementType,
        deliveryType: params.deliveryType,
        currency: params.currency,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function getSalesAgreementPricePeriods(
  params: GetSalesAgreementPricePeriodsParams,
): Promise<PricePeriod[]> {
  const response = await ApiClient.get<PricePeriod[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function getSalesAgreementPricePeriod(
  params: GetSalesAgreementPricePeriodParams,
): Promise<PricePeriodDetail> {
  const response = await ApiClient.get<PricePeriodDetail>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function getSalesAgreementAssortmentPrices(
  params: GetSalesAgreementAssortmentPricesParams,
): Promise<AssortmentPrice[]> {
  const response = await ApiClient.get<AssortmentPrice[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-prices`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function batchUpdateSalesAgreementAssortmentPrices(
  params: BatchUpdateAssortmentPricesParams,
  body: BatchUpdateAssortmentPriceItem[],
): Promise<AssortmentPrice[]> {
  const response = await ApiClient.patch<AssortmentPrice[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-prices`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function createSalesAgreementAssortmentPrice(
  params: CreateSalesAgreementAssortmentPriceParams,
  body: CreateSalesAgreementAssortmentPriceBody,
): Promise<AssortmentPrice> {
  const response = await ApiClient.post<AssortmentPrice>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-prices`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function updateSalesAgreementAssortmentPrice(
  params: UpdateSalesAgreementAssortmentPriceParams,
  body: UpdateSalesAgreementAssortmentPriceBody,
): Promise<AssortmentPrice> {
  const response = await ApiClient.put<AssortmentPrice>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-prices/${encodeURIComponent(params.assortmentPriceId)}`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function deleteSalesAgreementAssortmentPrice(
  params: DeleteSalesAgreementAssortmentPriceParams,
): Promise<void> {
  const response = await ApiClient.delete<void>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-prices/${encodeURIComponent(params.assortmentPriceId)}`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export async function getZonePrices(params: ZonePricesParams): Promise<ZonePrice[]> {
  const response = await ApiClient.get<ZonePrice[]>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/zone-prices`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function addZonePrices(
  params: AddZonePricesParams,
  body: AddZonePricesBody,
): Promise<ZonePrice[]> {
  const response = await ApiClient.post<ZonePrice[]>(
    `/timber-buyers/${params.timberBuyerId}/sales-agreements/${params.salesAgreementId}/price-periods/${params.pricePeriodId}/zone-prices`,
    body,
    {
      headers: params.etag ? { 'If-Match': params.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function batchUpdateZonePriceAdjustments(
  params: BatchUpdateZonePriceAdjustmentsParams,
  body: BatchUpdateZonePriceAdjustmentItem[],
): Promise<ZonePrice[]> {
  const response = await ApiClient.patch<ZonePrice[]>(
    `/timber-buyers/${params.timberBuyerId}/sales-agreements/${params.salesAgreementId}/price-periods/${params.pricePeriodId}/zone-prices/assortment-category-adjustments`,
    body,
    { headers: params.etag ? { 'If-Match': params.etag } : undefined },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function deleteZonePrice(params: DeleteZonePriceParams): Promise<void> {
  await apiDelete<void>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/zone-prices/${encodeURIComponent(params.municipalityNumber)}`,
    {
      headers: params.etag ? { 'If-Match': params.etag } : undefined,
    },
  )
}

export async function createSalesAgreementPricePeriod(
  params: CreateSalesAgreementPricePeriodParams,
  body: CreateSalesAgreementPricePeriodBody,
): Promise<PricePeriod> {
  const response = await ApiClient.post<PricePeriod>(
    `/timber-buyers/${params.timberBuyerId}/sales-agreements/${params.salesAgreementId}/price-periods`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function copySalesAgreementPricePeriod(
  params: CopySalesAgreementPricePeriodParams,
  body: CopySalesAgreementPricePeriodBody,
): Promise<PricePeriodDetail> {
  const response = await ApiClient.post<PricePeriodDetail>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/copy`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function updateSalesAgreementPricePeriod(
  params: UpdateSalesAgreementPricePeriodParams,
  body: UpdateSalesAgreementPricePeriodBody,
): Promise<PricePeriodDetail> {
  const response = await ApiClient.put<PricePeriodDetail>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}`,
    body,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

const getAssortmentCategoriesPath = (params: GetSalesAgreementAssortmentCategoriesParams): string =>
  `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/sales-agreements/${encodeURIComponent(params.salesAgreementId)}/price-periods/${encodeURIComponent(params.pricePeriodId)}/assortment-categories`

export async function getSalesAgreementAssortmentCategories(
  params: GetSalesAgreementAssortmentCategoriesParams,
): Promise<AssortmentCategory[]> {
  const response = await ApiClient.get<AssortmentCategory[]>(getAssortmentCategoriesPath(params), {
    params: {
      sortBy: params.sort?.sortBy,
      sortOrder: params.sort?.sortOrder,
    },
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function createSalesAgreementAssortmentCategory(
  params: CreateAssortmentCategoryParams,
  body: CreateAssortmentCategoryBody,
): Promise<AssortmentCategory> {
  const response = await ApiClient.post<AssortmentCategory>(
    getAssortmentCategoriesPath(params),
    body,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function assignAssortmentsToCategory(
  params: AssignAssortmentsToCategoryParams,
  body: AssignAssortmentsToCategoryBody,
): Promise<AssortmentCategory> {
  const response = await ApiClient.put<AssortmentCategory>(
    `${getAssortmentCategoriesPath(params)}/${encodeURIComponent(params.categoryId)}/assortments`,
    body,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function unassignAssortmentFromCategory(
  params: UnassignAssortmentFromCategoryParams,
): Promise<AssortmentCategory> {
  const response = await ApiClient.delete<AssortmentCategory>(
    `${getAssortmentCategoriesPath(params)}/${encodeURIComponent(params.categoryId)}/assortments/${encodeURIComponent(params.assortmentNumber)}`,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function renameSalesAgreementAssortmentCategory(
  params: RenameAssortmentCategoryParams,
  body: RenameAssortmentCategoryBody,
): Promise<AssortmentCategory> {
  const response = await ApiClient.put<AssortmentCategory>(
    `${getAssortmentCategoriesPath(params)}/${encodeURIComponent(params.categoryId)}`,
    body,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function deleteSalesAgreementAssortmentCategory(
  params: DeleteAssortmentCategoryParams,
): Promise<void> {
  const response = await ApiClient.delete<void>(
    `${getAssortmentCategoriesPath(params)}/${encodeURIComponent(params.categoryId)}`,
    {
      headers: { 'If-Match': params.etag },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export async function updateSalesAgreement(
  data: SalesAgreement,
  body: UpdateSalesAgreementBody,
): Promise<SalesAgreement> {
  const response = await ApiClient.put<SalesAgreement>(
    `/timber-buyers/${data.timberBuyerId}/sales-agreements/${data.id}`,
    body,
    {
      headers: data.etag ? { 'If-Match': data.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const updatedEtag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag: updatedEtag }
}

export async function deleteSalesAgreement(
  timberBuyerId: string,
  salesAgreementId: string,
  etag?: string,
): Promise<void> {
  await apiDelete<void>(
    `/timber-buyers/${encodeURIComponent(timberBuyerId)}/sales-agreements/${encodeURIComponent(salesAgreementId)}`,
    {
      headers: etag ? { 'If-Match': etag } : undefined,
    },
  )
}

export async function deletePricePeriod(
  timberBuyerId: string,
  salesAgreementId: string,
  pricePeriodId: string,
): Promise<void> {
  await apiDelete<void>(
    `/timber-buyers/${encodeURIComponent(timberBuyerId)}/sales-agreements/${encodeURIComponent(salesAgreementId)}/price-periods/${encodeURIComponent(pricePeriodId)}`,
  )
}

async function updateSalesAgreementStatus(
  timberBuyerId: string,
  salesAgreementId: string,
  status: 'Active' | 'Inactive',
): Promise<SalesAgreement> {
  const response = await ApiClient.put<SalesAgreement>(
    `/timber-buyers/${encodeURIComponent(timberBuyerId)}/sales-agreements/${encodeURIComponent(salesAgreementId)}/status`,
    { status },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const updatedEtag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag: updatedEtag }
}

export async function deactivateSalesAgreement(
  timberBuyerId: string,
  salesAgreementId: string,
): Promise<SalesAgreement> {
  return updateSalesAgreementStatus(timberBuyerId, salesAgreementId, 'Inactive')
}

export async function restoreSalesAgreement(
  timberBuyerId: string,
  salesAgreementId: string,
): Promise<SalesAgreement> {
  return updateSalesAgreementStatus(timberBuyerId, salesAgreementId, 'Active')
}

export async function updateMainAssortments(
  data: SalesAgreement,
  body: MainAssortmentModel[],
): Promise<SalesAgreement> {
  const response = await ApiClient.put<SalesAgreement>(
    `/timber-buyers/${data.timberBuyerId}/sales-agreements/${data.id}/main-assortments`,
    body,
    {
      headers: data.etag ? { 'If-Match': data.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const updatedEtag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag: updatedEtag }
}

const timberBuyerPricePeriodsPageSize = 50

export async function getTimberBuyerPricePeriods(
  params: GetTimberBuyerPricePeriodsParams,
): Promise<PagedResult<TimberBuyerPricePeriod>> {
  return apiGetWithPagedResult<TimberBuyerPricePeriod>(
    `/timber-buyers/${encodeURIComponent(params.timberBuyerId)}/price-periods`,
    { pageSize: params.pageSize ?? timberBuyerPricePeriodsPageSize },
    params.searchTerm ? { searchTerm: params.searchTerm } : undefined,
  )
}
